import React, { useState, useRef, useCallback } from "react";
import ThemePicker from "./ThemePicker";
import {
  LayoutTemplate,
  Car,
  Users,
  LogOut,
  Settings as SettingsIcon,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  File,
  Trash2,
  Upload,
  PanelLeft,
  Play,
  Square,
  CheckCircle,
  Clock,
  Loader,
  XCircle,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Building2,
  CheckSquare,
  Search,
} from "lucide-react";
import { MENU_ITEMS, resolveVisibleMenus, groupLoginHsMenus, ITEM_GROUP_ID, ITEM_GROUP_IDS } from "../config/menuConfig";
import { isLoginHSProduct } from "../config/product";
import LeftAiPanel from "../audit/LeftAiPanel";
import ClassificationWorkspaceNav from "../audit/ClassificationWorkspaceNav";
import { useAuditSearch } from "../audit/AuditSearchContext";

const Sidebar = ({
  width = 240, // 사용자 드래그 리사이즈 폭 (App.js sidebarWidth)
  activeTab,
  setActiveTab,
  onLogout,
  onOpenAccountSettings,
  userGroup,
  userId,
  isManager = false,
  menuVisibility = null, // null = 로딩 전 — 기본값(hiddenByDefault) 사용
  onOpenPromptManager,
  fileList = [],
  onFileSelect,
  selectedFileIndex,
  onToggleFileCheck,
  onToggleAllChecks,
  onRemoveFile,
  onRemoveCheckedFiles,
  onExtract,
  isExtracting,
  onDrop,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onFileInputChange,
  isOpen,
  onToggle,
  onBatchExtract,
  isBatchExtracting,
  schedulerEnabled,
  onToggleScheduler,
  onOpenMappingRules,
  legacyFiles = [],
  legacyFilesTotal = 0,
  legacyFilesLoading = false,
  onLegacyFileClick,
  onToggleLegacyCheck,
  children,
}) => {
  // Claude-style: 열림/닫힘은 부모(App.js)에서 제어
  const showExpanded = isOpen;

  const {
    setSearchContext,
    setFocusedCase,
    setGoogleImageQ,
    setSearchResults,
    setSubdivisionPreview,
  } = useAuditSearch();

  // ── 관세청식 아코디언: 통관정보/관세 제도 하위를 사이드바에 중첩(우측 서브패널 대체) ──
  //   한 번에 한 그룹만 펼침(검색 밀림 방지). count = /api/onto/req-guide/counts.
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [acctOpen, setAcctOpen] = useState(false);
  const [reqCounts, setReqCounts] = useState({});
  const [, bumpNav] = useState(0); // popstate 시 재렌더(활성 하위 하이라이트 갱신)
  React.useEffect(() => {
    fetch("/api/onto/req-guide/counts")
      .then((r) => (r.ok ? r.json() : {}))
      .then(setReqCounts)
      .catch(() => {});
  }, []);
  React.useEffect(() => {
    const h = () => bumpNav((n) => n + 1);
    window.addEventListener("popstate", h);
    return () => window.removeEventListener("popstate", h);
  }, []);
  React.useEffect(() => {
    const g =
      activeTab === "hs-req-guide"
        ? "hs-req-guide"
        : activeTab === "hs-tools"
          ? "hs-tools"
        : ITEM_GROUP_IDS.includes(activeTab)
          ? ITEM_GROUP_ID
        : ["hs-ai-classify", "hs-compare", "hs-precedents-explorer"].includes(
              activeTab,
            )
          ? "hs-ai-classify"
          : [
                "hs-customs-guide",
                "hs-exemption",
                "hs-simplified-rate",
                "hs-installment",
                "hs-enduse-rate",
              ].includes(activeTab)
            ? "hs-customs-guide"
            : null;
    if (g) setExpandedGroup(g);
  }, [activeTab]);
  const navHref = (href) => {
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  };
  const childIsActive = (href) => {
    const cur = new URLSearchParams(window.location.search);
    const ch = new URLSearchParams(href.split("?")[1] || "");
    if (ch.get("page")) return cur.get("page") === ch.get("page");
    if ((cur.get("audit") || "") !== (ch.get("audit") || "")) return false;
    if (ch.get("cat") && cur.get("cat") !== ch.get("cat")) return false;
    if (ch.get("tool") && cur.get("tool") !== ch.get("tool")) return false;
    return true;
  };

  const checkedCount = fileList.filter((f) => f.checked).length;

  // Shift+클릭 범위 선택용
  // anchor = 마지막으로 클릭한 항목의 키(현재파일 `c{index}` / 이력 `legacy-{sq}`).
  const lastCheckedRef = useRef(null);
  // 표시(날짜 desc 정렬) 순서의 "전체" 아이템(현재+이력). 범위는 fileList 인덱스가 아니라
  // 화면에 보이는 순서를 따라야 하므로(목록이 날짜 desc 정렬). 아래 unifiedGroups 계산 후 매 렌더 갱신.
  const orderRef = useRef([]);
  const keyOfItem = (it) => (it.type === "current" ? `c${it.index}` : it.id);
  // 범위 내 한 항목을 "추가 선택"(additive): 현재=미체크면 체크, 이력=로드 후 합류
  const selectItemAdditive = (it) => {
    if (it.type === "current") {
      if (!fileList[it.index]?.checked)
        onToggleFileCheck && onToggleFileCheck(it.index);
    } else if (it.type === "legacy" && onToggleLegacyCheck) {
      onToggleLegacyCheck(it.legacy);
    }
  };
  const handleShiftCheck = useCallback(
    (item, e) => {
      const order = orderRef.current || [];
      const curKey = keyOfItem(item);
      const anchorKey = lastCheckedRef.current;
      if (e.shiftKey && anchorKey != null && anchorKey !== curKey) {
        const aPos = order.findIndex((it) => keyOfItem(it) === anchorKey);
        const cPos = order.findIndex((it) => keyOfItem(it) === curKey);
        if (aPos !== -1 && cPos !== -1) {
          const [s, e2] = aPos < cPos ? [aPos, cPos] : [cPos, aPos];
          for (let p = s; p <= e2; p++) selectItemAdditive(order[p]);
          lastCheckedRef.current = curKey;
          return;
        }
      }
      // 단일 클릭: 현재=토글, 이력=로드 후 합류
      if (item.type === "current") {
        onToggleFileCheck && onToggleFileCheck(item.index);
      } else if (item.type === "legacy" && onToggleLegacyCheck) {
        onToggleLegacyCheck(item.legacy);
      }
      lastCheckedRef.current = curKey;
    },
    [fileList, onToggleFileCheck, onToggleLegacyCheck],
  );

  // ChatGPT/Claude 스타일: 통합 히스토리 (날짜별 그룹핑)
  const unifiedGroups = React.useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 30);

    const items = [];

    // 현재 세션 파일 (가장 최근 업로드 = 배열 끝 → 가장 상단)
    fileList.forEach((f, idx) => {
      items.push({
        type: "current",
        index: idx,
        id: f.id || `file-${idx}`,
        name: f.name,
        date: f._uploadedAt
          ? new Date(f._uploadedAt)
          : new Date(now.getTime() - (fileList.length - 1 - idx)),
        status: f.status,
        extractedData: f.extractedData,
        batchStatus: f.batchStatus,
        checked: !!f.checked,
        isPDF: f.isPDF,
        file: f,
      });
    });

    // 레거시 파일 (중복 제외)
    legacyFiles.forEach((lf) => {
      const isDup = fileList.some(
        (f) => f.id === `legacy-${lf.sq}` || f.name === lf.fileName,
      );
      if (isDup) return;
      items.push({
        type: "legacy",
        id: `legacy-${lf.sq}`,
        name: lf.fileName || `file_${lf.sq}`,
        date: lf.regDt ? new Date(lf.regDt) : new Date(0),
        userId: lf.userId,
        legacy: lf,
      });
    });

    items.sort((a, b) => b.date - a.date);

    const labels = [
      { key: "today", label: "오늘", test: (d) => d >= today },
      { key: "yesterday", label: "어제", test: (d) => d >= yesterday },
      { key: "week", label: "이전 7일", test: (d) => d >= weekAgo },
      { key: "month", label: "이전 30일", test: (d) => d >= monthAgo },
      { key: "older", label: "이전", test: () => true },
    ];

    const groups = [];
    const groupMap = {};
    items.forEach((item) => {
      for (const { key, label, test } of labels) {
        if (test(item.date)) {
          if (!groupMap[key]) {
            groupMap[key] = { key, label, items: [] };
            groups.push(groupMap[key]);
          }
          groupMap[key].items.push(item);
          break;
        }
      }
    });
    return groups;
  }, [fileList, legacyFiles]);

  const totalFileCount = unifiedGroups.reduce(
    (sum, g) => sum + g.items.length,
    0,
  );

  // 🔧 Shift 범위 선택용 — 표시 순서(날짜정렬)의 전체 아이템(현재+이력)으로 매 렌더 갱신
  orderRef.current = unifiedGroups.flatMap((g) => g.items);

  // 🔧 메뉴 가시성:
  //   - source of truth: src/config/menuConfig.js (MENU_ITEMS)
  //   - MANAGER_YN='Y' 사용자(isManager=true) 는 항상 전체 노출
  //   - 일반 사용자는 menuVisibility (백엔드 D_DOCUAI_MENU_VISIBILITY) 적용
  //   - menuVisibility null = 로딩 전 → hiddenByDefault 적용
  let menuItems = resolveVisibleMenus(MENU_ITEMS, menuVisibility, isManager);
  // loginHS 제품: HS 분류 메뉴(category 'hs')만 노출 — 문서추출/학습/기타 숨김.
  //   (isManager 라도 loginHS 도메인에선 HS 만. 같은 build, 도메인 분기.)
  if (isLoginHSProduct()) {
    // 상단 메뉴 최소화(사용자 요청) — HS 카테고리 중 문의·건의 제외(하단 사용자 영역으로 이동).
    //   딥러닝(docuai2 외부링크)도 상단 삽입하지 않고 맨 하단에 별도 배치(아래 렌더).
    menuItems = menuItems.filter(
      (m) => m.category === "hs" && m.id !== "inquiry-board",
    );
  }
  // 2026-10-01: 품목 3개는 한 묶음, 사용량·회사 정보는 계정 메뉴로(권한 적용 뒤).
  let accountMenus = [];
  if (isLoginHSProduct()) {
    const grouped = groupLoginHsMenus(menuItems);
    menuItems = grouped.menus;
    accountMenus = grouped.account;
  }

  return (
    <div className="flex h-full flex-shrink-0 z-20 relative transition-all duration-300">
      {/* Nav Strip — Claude-style (클릭으로만 열고 닫기). 배경 = audit 영역과 동일 토큰 */}
      <div
        className={`
                    border-r flex flex-col z-30 h-full
                    ${showExpanded ? "" : "w-[48px] items-center"}
                `}
        style={{
          backgroundColor: "var(--audit-bg)",
          borderColor: "var(--audit-border)",
          // 드래그 리사이즈 폭 — 드래그 중 transition 이 따라오면 덜컹거려 transition 제거
          ...(showExpanded ? { width } : {}),
        }}
      >
        {/* Top: Logo + Toggle (Claude-style) */}
        {showExpanded ? (
          <div className="flex items-center justify-between w-full pt-4 pb-3 px-3">
            <div
              className={`flex items-center cursor-pointer group/logo ${isLoginHSProduct() ? "" : "gap-2.5"}`}
              onClick={() => {
                // 로고 클릭 → 홈(loginHS=대시보드, DocuAI=메인). "/"면 _loginHsHome 발동(2026-07-14 배선).
                window.history.pushState({}, "", "/");
                window.dispatchEvent(new PopStateEvent("popstate"));
              }}
              title="홈으로"
            >
              {!isLoginHSProduct() && <div className="w-8 h-8 rounded-lg bg-[#111827] flex items-center justify-center shadow-sm transition-all duration-200 group-hover/logo:shadow-[0_3px_12px_rgba(66,133,244,0.4)] group-hover/logo:scale-105">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 48 48"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <g stroke="#fff" strokeWidth="5">
                    <line x1="14" y1="12" x2="14" y2="36" />
                    <line x1="34" y1="12" x2="34" y2="36" />
                  </g>
                  <polyline
                    points="17,24 23,29 31,18"
                    stroke="#4285F4"
                    strokeWidth="4.8"
                    fill="none"
                  />
                </svg>
              </div>}
              <span
                className="brand-wordmark text-[18px] font-bold tracking-tight"
                style={{ color: "#141413", letterSpacing: "-0.02em" }}
              >
                {isLoginHSProduct() ? "LoginHS" : "DocuAI"}
              </span>
              {isLoginHSProduct() && (
                <span
                  title="현재 일부 고객사와 함께 기능을 고도화하고 있습니다."
                  style={{
                    marginLeft: 7,
                    padding: "1px 5px",
                    border: "1px solid #d6d3d1",
                    borderRadius: 999,
                    background: "#f5f5f4",
                    color: "#78716c",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    lineHeight: 1.4,
                  }}
                >
                  BETA
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={onToggle}
                className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 hover:text-[#141413] transition-colors"
                title="사이드바 닫기"
              >
                <PanelLeft size={20} strokeWidth={1.75} />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center w-full pt-4 pb-3">
            <button
              onClick={onToggle}
              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-200 hover:text-[#141413] transition-colors"
              title="사이드바 열기"
            >
              <PanelLeft size={20} strokeWidth={1.75} />
            </button>
          </div>
        )}

        {/* 딥러닝 Button — DocuAI 관리자 전용(내부 viewer). loginHS 는 메뉴의 '딥러닝 ↗'(docuai2)만. */}
        {!isLoginHSProduct() && isManager && (
          <div
            className={`pb-1 w-full relative ${showExpanded ? "px-2" : "flex justify-center"}`}
          >
            <button
              onClick={() => {
                // audit 페이지(AI HS분류/관세청품목분류사례)에서 클릭 = 딥러닝 메뉴
                // 복귀만 — 추출 자동 실행 안 함 (사용자가 다시 눌러야 추출).
                if (new URLSearchParams(window.location.search).get("audit")) {
                  window.history.pushState({}, "", "/");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                  setActiveTab("viewer");
                  return;
                }
                // 딥러닝 화면에서 클릭 = 선택 파일 추출 (기존 동작)
                setActiveTab("viewer");
                if (onExtract) onExtract();
              }}
              disabled={isExtracting}
              className={`
                            flex items-center transition-colors duration-150 relative
                            ${
                              showExpanded
                                ? "w-full px-3 py-2 gap-2.5 justify-start rounded-lg text-[13px]"
                                : "w-9 h-9 justify-center rounded-lg"
                            }
                            ${
                              isExtracting
                                ? "bg-gray-400 text-white cursor-wait"
                                : showExpanded
                                  ? "bg-gray-200 text-[#141413] hover:bg-gray-300"
                                  : "bg-gray-900 text-white hover:bg-[#f5c518] hover:text-[#141413]"
                            }
                            }
                        `}
              title={!showExpanded ? "딥러닝" : ""}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 48 48"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <g stroke="#fff" strokeWidth="5">
                  <line x1="14" y1="12" x2="14" y2="36" />
                  <line x1="34" y1="12" x2="34" y2="36" />
                </g>
                <polyline
                  points="17,24 23,29 31,18"
                  stroke="#4285F4"
                  strokeWidth="4.8"
                  fill="none"
                />
              </svg>
              {showExpanded && (
                <span className="whitespace-nowrap overflow-hidden truncate font-semibold flex-1 text-left">
                  딥러닝
                </span>
              )}
            </button>
          </div>
        )}

        {/* Other Menu Items */}
        {(!isLoginHSProduct() || showExpanded) && <div
          className={`flex flex-col pb-2 w-full ${showExpanded ? "px-2 space-y-0.5" : "items-center space-y-1"}`}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            const hasChildren =
              showExpanded &&
              Array.isArray(item.children) &&
              item.children.length > 0;
            const expanded = expandedGroup === item.id;

            const navigateItem = () => {
              // 크로스 링크(딥러닝 → docuai2): 이름있는 탭("docuai2") 재사용 + SSO 토큰 전달
              if (item._external) {
                const tok = localStorage.getItem("token");
                const url = tok
                  ? `${item._external}#sso=${encodeURIComponent(tok)}`
                  : item._external;
                window.open(url, "docuai2");
                return;
              }
              if (item.href) {
                if (item.spa) {
                  // 리로드 없이 — App 의 popstate 리스너가 _auditRoute 갱신
                  window.history.pushState({}, "", item.href);
                  window.dispatchEvent(new PopStateEvent("popstate"));
                } else {
                  window.location.href = item.href;
                }
              } else {
                // 메인 앱 탭 — audit overlay 가 떠 있으면 URL ?audit 제거
                if (new URLSearchParams(window.location.search).get("audit")) {
                  window.history.pushState({}, "", "/");
                  window.dispatchEvent(new PopStateEvent("popstate"));
                }
                setActiveTab(item.id);
              }
            };

            return (
              <React.Fragment key={item.id}>
                <button
                  onClick={() => {
                    if (hasChildren) {
                      // 항목 전체가 토글 — 접힘 상태면 펼치며 허브로 이동, 펼침 상태면 접기(내용 유지).
                      const willExpand = !expanded;
                      setExpandedGroup(willExpand ? item.id : null);
                      if (willExpand && item.href) navigateItem();
                    } else {
                      navigateItem();
                    }
                  }}
                  className={`
                                    flex items-center transition-colors duration-150 relative
                                    ${
                                      showExpanded
                                        ? "w-full px-3 py-2 gap-2.5 justify-start rounded-lg text-[13px]"
                                        : "w-9 h-9 justify-center rounded-lg"
                                    }
                                    ${
                                      isActive
                                        ? "bg-gray-200/70 text-gray-900 font-bold"
                                        : "text-gray-800 font-normal hover:bg-gray-200/50 hover:text-gray-900"
                                    }
                                `}
                  title={!showExpanded ? item.label : ""}
                >
                  <Icon size={18} strokeWidth={isActive ? 2 : 1.5} />
                  {/* 맑은 고딕은 보통·굵게 두 굵기뿐이라 semibold 는 굵게로 그려진다 — 굵기는 버튼(선택=굵게)을 따른다 */}
                  {showExpanded && (
                    <span className="whitespace-nowrap overflow-hidden truncate flex-1 text-left">
                      {item.label}
                    </span>
                  )}
                  {showExpanded && item.beta && (
                    <span className="flex-shrink-0 text-[10px] font-normal text-gray-500 border border-gray-300 rounded px-1 leading-4">
                      beta
                    </span>
                  )}
                  {hasChildren && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedGroup(expanded ? null : item.id);
                      }}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 cursor-pointer"
                      title={expanded ? "접기" : "펼치기"}
                    >
                      {expanded ? (
                        <ChevronDown size={15} />
                      ) : (
                        <ChevronRight size={15} />
                      )}
                    </span>
                  )}
                </button>
                {hasChildren && expanded && (
                  <div className="mt-0.5 mb-1 space-y-[2px]">
                    {item.children.map((child) => {
                      const cActive = childIsActive(child.href);
                      const cnt =
                        child.countKey != null
                          ? reqCounts[child.countKey]
                          : null;
                      return (
                        <button
                          key={child.href}
                          onClick={() => navHref(child.href)}
                          className={`flex items-center w-full pl-9 pr-3 py-[7px] gap-2 rounded-lg text-[12px] ${
                            cActive
                              ? "bg-gray-200/70 text-gray-900 font-bold"
                              : "text-gray-700 hover:bg-gray-200/50 hover:text-gray-900"
                          }`}
                        >
                          <span className="flex-1 min-w-0 truncate text-left">
                            {child.label}
                          </span>
                          {child.beta && (
                            <span className="flex-shrink-0 text-[10px] leading-4 px-1 rounded border border-gray-300 text-gray-400">
                              beta
                            </span>
                          )}
                          {cnt != null && (
                            <span className="flex-shrink-0 text-[11px] text-gray-400 tabular-nums">
                              {Number(cnt).toLocaleString()}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>}

        {/* Separator */}
        {(!isLoginHSProduct() || showExpanded) && <div
          className={`border-t border-gray-200 ${showExpanded ? "mx-3" : "mx-2"} my-1`}
        />}

        {/* loginHS: 문서처리 파일패널 대신 AI 검색(LeftAiPanel)을 사이드바에 통합.
            검색은 사이드바 한 곳 — audit 페이지 좌측 패널은 제거(HeadingDetailPage loginHS 2컬럼).
            ⚠ LeftAiPanel = pendingSearch(가운데/상단 검색바 제출) 유일 소비자(doSearch).
            접힘(showExpanded=false)에도 마운트 유지(display:none) — 언마운트되면 접힘 상태
            플로팅/상단 검색 제출이 소비자 없이 사라진다. 결과는 가운데 6탭 뷰에 표시. */}
        {isLoginHSProduct() && showExpanded && (
          <div className="w-full flex-1 min-h-0 px-2 pt-1">
            <ClassificationWorkspaceNav />
          </div>
        )}

        {/* 상단 통합 검색의 실행 엔진. 화면에서는 중복 검색 UI를 숨기되 mount는 유지한다. */}
        {isLoginHSProduct() && (
          <div className="hidden" aria-hidden="true">
            <LeftAiPanel
              currentChapter={null}
              onAnyAction={() => {}}
              onSearchContextChange={setSearchContext}
              onFocusCase={setFocusedCase}
              onGoogleImageOpen={setGoogleImageQ}
              onSearchResults={setSearchResults}
              onSubdivisionPreview={setSubdivisionPreview}
            />
          </div>
        )}

        {/* File List Area */}
        {!isLoginHSProduct() && (
          <div
            className="w-full flex-1 overflow-hidden min-h-0 flex flex-col"
            onDragOver={onDragOver}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            {/* Header: 배치 작업 바 (체크된 파일 있을 때만) */}
            {showExpanded && checkedCount > 0 && (
              <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-100">
                <div className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={
                      fileList.length > 0 && fileList.every((f) => f.checked)
                    }
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          fileList.some((f) => f.checked) &&
                          !fileList.every((f) => f.checked);
                    }}
                    onChange={() => onToggleAllChecks && onToggleAllChecks()}
                    className="mono-checkbox"
                    title={
                      fileList.every((f) => f.checked)
                        ? "전체 해제"
                        : "전체 선택"
                    }
                  />
                  <span className="text-[12px] text-gray-500">
                    {checkedCount}개 선택
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {isBatchExtracting ? (
                    <button
                      onClick={() => onBatchExtract && onBatchExtract("cancel")}
                      className="p-0.5 text-orange-500 hover:text-red-600"
                      title="배치 추출 중지"
                    >
                      <Square size={13} fill="currentColor" />
                    </button>
                  ) : (
                    <button
                      onClick={() => onBatchExtract && onBatchExtract()}
                      className="p-0.5 text-gray-400 hover:text-green-600"
                      title={`${checkedCount}개 배치 추출`}
                    >
                      <Play size={13} fill="currentColor" />
                    </button>
                  )}
                  {!isBatchExtracting && (
                    <button
                      onClick={() =>
                        onRemoveCheckedFiles && onRemoveCheckedFiles()
                      }
                      className="p-0.5 text-gray-400 hover:text-red-600"
                      title={`${checkedCount}개 삭제`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 통합 히스토리 리스트 (날짜별 그룹핑) */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 custom-scrollbar">
              {legacyFilesLoading && totalFileCount === 0 && showExpanded && (
                <div className="flex items-center justify-center py-8">
                  <Loader size={16} className="text-gray-400 animate-spin" />
                  <span className="text-[12px] text-[#70757a] ml-2">
                    로딩 중...
                  </span>
                </div>
              )}

              {totalFileCount === 0 && !legacyFilesLoading && showExpanded && (
                <div className="flex flex-col items-center justify-center py-10 px-4 text-[#70757a]">
                  <FileText size={28} className="mb-2 text-gray-300" />
                  <span className="text-[12px]">파일이 없습니다</span>
                  <span className="text-[12px] mt-0.5">
                    드래그하거나 아래 버튼으로 추가하세요
                  </span>
                </div>
              )}

              {unifiedGroups.map((group) => (
                <div key={group.key}>
                  {/* 날짜 그룹 헤더 */}
                  {showExpanded && (
                    <div
                      className="px-3 pt-[10px] pb-1.5 sticky top-0 z-10"
                      style={{ backgroundColor: "var(--audit-bg)" }}
                    >
                      <span className="text-[12px] font-semibold text-[#70757a] tracking-wide uppercase">
                        {group.label}
                      </span>
                    </div>
                  )}
                  {/* 파일 목록 */}
                  <div
                    className={
                      showExpanded
                        ? "px-1.5 space-y-[2px]"
                        : "items-center px-1 space-y-1"
                    }
                  >
                    {(showExpanded
                      ? group.items
                      : group.items.filter(
                          (item) =>
                            item.type === "current" &&
                            item.index === selectedFileIndex,
                        )
                    ).map((item) => {
                      const isSelected =
                        item.type === "current" &&
                        selectedFileIndex === item.index;
                      const ext = item.name
                        ? item.name.split(".").pop().toLowerCase()
                        : "";
                      let ItemIcon = File;
                      let iconColor = "text-gray-400";
                      if (["xlsx", "xls", "csv"].includes(ext)) {
                        ItemIcon = FileSpreadsheet;
                        iconColor = "text-[#5f6368]";
                      } else if (item.isPDF || ext === "pdf") {
                        ItemIcon = FileText;
                        iconColor = "text-[#5f6368]";
                      } else if (
                        ["jpg", "jpeg", "png", "webp", "bmp"].includes(ext)
                      ) {
                        ItemIcon = ImageIcon;
                        iconColor = "text-[#5f6368]";
                      }

                      return (
                        <div
                          key={item.id}
                          className={`
                                                    group transition-colors duration-150 cursor-pointer relative
                                                    ${showExpanded ? "px-2 py-[6px] rounded-lg" : "w-9 h-9 flex items-center justify-center rounded-lg"}
                                                    ${isSelected ? "bg-[#d3e3fd]/50 text-[#1a1a1a]" : "hover:bg-[#f1f3f4] text-[#444746]"}
                                                `}
                          onClick={() => {
                            if (item.type === "current") {
                              onFileSelect &&
                                onFileSelect(item.file, item.index);
                            } else {
                              onLegacyFileClick &&
                                onLegacyFileClick(item.legacy);
                            }
                          }}
                          title={!showExpanded ? item.name : item.name}
                        >
                          <div
                            className={`flex items-center ${showExpanded ? "gap-1.5" : "justify-center"}`}
                          >
                            {/* 체크박스 (현재 세션 파일) */}
                            {showExpanded && item.type === "current" && (
                              <input
                                type="checkbox"
                                checked={item.checked}
                                onChange={() => {}}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShiftCheck(item, e);
                                }}
                                className="mono-checkbox flex-shrink-0"
                                style={{ width: 13, height: 13 }}
                              />
                            )}
                            {/* 이력 파일 체크박스 — 선택 시 DB 에서 로드 후 배치에 합류 */}
                            {showExpanded && item.type === "legacy" && (
                              <input
                                type="checkbox"
                                checked={false}
                                onChange={() => {}}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShiftCheck(item, e);
                                }}
                                className="mono-checkbox flex-shrink-0"
                                style={{ width: 13, height: 13 }}
                                title="이력 파일 — 선택하면 로드 후 현재 목록(배치)에 추가됩니다"
                              />
                            )}

                            <ItemIcon
                              size={15}
                              className={`flex-shrink-0 ${isSelected ? "text-[#1967d2]" : iconColor}`}
                            />

                            {showExpanded && (
                              <div className="flex-1 min-w-0">
                                <div
                                  className={`text-[14px] leading-5 truncate ${isSelected ? "font-medium text-[#1a1a1a]" : "text-[#3c4043]"}`}
                                >
                                  {item.name}
                                </div>
                                {/* 레거시 파일: 사용자 ID 작게 표시 */}
                                {item.type === "legacy" && item.userId && (
                                  <div className="text-[11.5px] text-[#70757a] truncate leading-4">
                                    {item.userId}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* 상태 아이콘 (현재 세션 파일) */}
                            {showExpanded && item.type === "current" && (
                              <span className="flex-shrink-0">
                                {item.batchStatus === "extracting" ? (
                                  <Loader
                                    size={12}
                                    className="text-blue-500 animate-spin"
                                  />
                                ) : item.batchStatus === "queued" ? (
                                  <Clock size={12} className="text-gray-400" />
                                ) : item.batchStatus === "error" ? (
                                  <XCircle size={12} className="text-red-500" />
                                ) : item.batchStatus === "completed" ||
                                  item.extractedData ? (
                                  <CheckCircle
                                    size={12}
                                    className="text-green-500"
                                  />
                                ) : null}
                              </span>
                            )}

                            {/* 삭제 버튼 (hover 시 표시) */}
                            {showExpanded && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.type === "current") {
                                    onRemoveFile && onRemoveFile(item.index);
                                  }
                                }}
                                className={`opacity-0 group-hover:opacity-100 p-0.5 hover:text-red-500 text-gray-400 transition-opacity flex-shrink-0 ${item.type === "legacy" ? "invisible" : ""}`}
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Upload Icon (Collapsed Only) */}
            {!showExpanded && (
              <div className="flex flex-col items-center pb-2 mt-auto">
                <input
                  type="file"
                  id="sidebar-file-upload-collapsed"
                  className="hidden"
                  accept="image/*,.tif,.tiff,application/pdf,.xlsx,.xls,.xlsm,.docx,.doc,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                  multiple
                  onChange={onFileInputChange}
                />
                <button
                  onClick={() =>
                    document
                      .getElementById("sidebar-file-upload-collapsed")
                      .click()
                  }
                  className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-200/50 hover:text-blue-600 transition-colors"
                  title="파일 추가"
                >
                  <Upload size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ═══ Fixed Bottom Section ═══ */}

        {/* Scheduler Toggle + Mapping Rules (Fixed) */}
        {!isLoginHSProduct() && showExpanded && fileList.length > 0 && (
          <div className="flex items-center justify-between px-4 py-1.5 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-gray-500 font-medium">
                스케줄러
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMappingRules && onOpenMappingRules();
                }}
                className="text-[11px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600 border border-gray-200 transition-colors"
                title="파일명-템플릿 매핑 규칙 관리"
              >
                매핑규칙
              </button>
            </div>
            <button
              onClick={() => onToggleScheduler && onToggleScheduler()}
              className={`relative w-8 h-4 rounded-full transition-colors duration-200 ${schedulerEnabled ? "bg-blue-500" : "bg-gray-300"}`}
              title={schedulerEnabled ? "스케줄러 OFF" : "스케줄러 ON"}
            >
              <span
                className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform duration-200 ${schedulerEnabled ? "translate-x-4.5 left-[18px]" : "left-[2px]"}`}
              />
            </button>
          </div>
        )}

        {/* Upload Area (Fixed at Bottom) */}
        {!isLoginHSProduct() && showExpanded && (
          <div className="px-2 pb-2 pt-1.5 border-t border-gray-100">
            <input
              type="file"
              id="sidebar-file-upload"
              className="hidden"
              accept="image/*,.tif,.tiff,application/pdf,.xlsx,.xls,.xlsm,.docx,.doc,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              multiple
              onChange={onFileInputChange}
            />
            <button
              onClick={() =>
                document.getElementById("sidebar-file-upload").click()
              }
              className="w-full flex flex-col items-center justify-center gap-1 px-3 py-3 border-2 border-dashed border-gray-200 rounded-lg text-gray-400 hover:text-blue-600 hover:border-blue-300 hover:bg-blue-50/50 transition-all group"
            >
              <div className="flex items-center gap-2 text-[13px] text-gray-600 group-hover:text-blue-600 font-medium">
                <Upload size={16} />
                <span>파일 추가</span>
              </div>
              <span className="text-[11px] text-gray-400 group-hover:text-blue-500/80 leading-relaxed text-center">
                화면 어디에나 파일을 드래그하여 놓으세요
              </span>
            </button>
          </div>
        )}

        {/* Bottom: User Info */}
        <div
          className={`border-t border-gray-200 w-full ${showExpanded ? "px-2 py-2" : "flex justify-center py-2"}`}
        >
          {showExpanded ? (
            <div className="relative flex items-center gap-2 px-1">
              {acctOpen && (
                <div
                  data-testid="account-menu"
                  className="absolute bottom-[calc(100%+6px)] left-0 right-0 z-[140] rounded-xl border border-gray-200 bg-white p-1.5 text-[12px] shadow-xl"
                  onMouseLeave={() => setAcctOpen(false)}
                >
                  <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold text-gray-500">내 계정</div>
                  {onOpenAccountSettings && (
                    <button type="button" onClick={() => { setAcctOpen(false); onOpenAccountSettings(); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-gray-800 hover:bg-gray-100">
                      <SettingsIcon size={14} /> 계정 설정
                    </button>
                  )}
                  {accountMenus.map((m) => {
                    const MIcon = m.icon;
                    return (
                      <button key={m.id} type="button" onClick={() => { setAcctOpen(false); navHref(m.href); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-gray-800 hover:bg-gray-100">
                        {MIcon ? <MIcon size={14} /> : null} {m.label}
                      </button>
                    );
                  })}
                  <div className="my-1 border-t border-gray-100" />
                  <button type="button" onClick={() => { setAcctOpen(false); onLogout(); }} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-gray-600 hover:bg-gray-100 hover:text-red-600">
                    <LogOut size={14} /> 로그아웃
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setAcctOpen((v) => !v)}
                aria-expanded={acctOpen}
                title="내 계정 · 사용량 · 회사 정보"
                className="flex flex-1 min-w-0 items-center gap-2 rounded-lg px-1 py-0.5 text-left hover:bg-gray-200/60"
              >
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-[11px] text-blue-700 font-bold flex-shrink-0">
                  {userGroup?.slice(0, 1)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-gray-800 truncate">
                    {userGroup}
                  </div>
                  <div className="text-[11px] text-gray-500 truncate">
                    {userId}
                  </div>
                </div>
              </button>
              <ThemePicker />
              {/* 문의·건의 → 상단 헤더로 이동, 홈페이지 링크 → 대시보드로 이동(2026-07-14). */}
              {onOpenAccountSettings && (
                <button
                  onClick={onOpenAccountSettings}
                  className="text-gray-400 hover:text-gray-700 flex-shrink-0 transition-colors"
                  title="내 계정 설정"
                >
                  <SettingsIcon size={14} />
                </button>
              )}
              <button
                onClick={onLogout}
                className="text-gray-400 hover:text-red-500 flex-shrink-0 transition-colors"
                title="로그아웃"
              >
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <ThemePicker collapsed />
              {onOpenAccountSettings && (
                <button
                  className="w-7 h-7 rounded flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
                  title={`${userGroup} · 계정 설정`}
                  onClick={onOpenAccountSettings}
                >
                  <SettingsIcon size={14} />
                </button>
              )}
              <button
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-bold text-gray-600 hover:bg-gray-300 transition-colors"
                title={`${userGroup} · 로그아웃`}
                onClick={onLogout}
              >
                {userGroup?.slice(0, 1)}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content Panel (Children) — Nav Strip 과 동일 톤 (audit 일원화) */}
      {children && (
        <div
          className="flex flex-col h-full border-r relative transition-none"
          style={{
            width: "320px",
            backgroundColor: "var(--audit-bg)",
            borderColor: "var(--audit-border)",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
